import React, { createContext, useContext, useState, useEffect } from 'react';

interface TimerOverride {
  baseSeconds: number;
  startedAt: number;
}

interface TimeDisplayOverride {
  startTime?: string;
  optiFinish?: string;
}

interface FilmScriptState {
  timerOverrides: Record<string, TimerOverride>;
  timeDisplayOverrides: Record<string, TimeDisplayOverride>;
}

interface MarketingModeContextType {
  isMarketingMode: boolean;
  isStormforgeMode: boolean;
  timerOverrides: Record<string, TimerOverride>;
  toggleMarketingMode: () => void;
  toggleStormforgeMode: () => void;
  activateFilmScript: () => void;
  getTimerOverride: (customerName: string) => TimerOverride | null;
  getTimeDisplayOverride: (customerName: string) => TimeDisplayOverride | null;
  notifyTimerStarted: (customerName: string) => void;
}

const MarketingModeContext = createContext<MarketingModeContextType | undefined>(undefined);

export function MarketingModeProvider({ children }: { children: React.ReactNode }) {
  const [isMarketingMode, setIsMarketingMode] = useState(() => {
    const stored = localStorage.getItem('marketingMode');
    return stored === 'true';
  });

  const [isStormforgeMode, setIsStormforgeMode] = useState(() => {
    const stored = localStorage.getItem('stormforgeMode');
    return stored === 'true';
  });

  const [filmScriptState, setFilmScriptState] = useState<FilmScriptState>({
    timerOverrides: {},
    timeDisplayOverrides: {},
  });

  useEffect(() => {
    localStorage.setItem('marketingMode', isMarketingMode.toString());
  }, [isMarketingMode]);

  useEffect(() => {
    localStorage.setItem('stormforgeMode', isStormforgeMode.toString());
  }, [isStormforgeMode]);

  // No watcher effect needed - all timers start immediately

  const toggleMarketingMode = () => {
    setIsMarketingMode(prev => {
      const newValue = !prev;
      // Reset film script state when turning OFF
      if (!newValue) {
        setFilmScriptState({
          timerOverrides: {},
          timeDisplayOverrides: {},
        });
      }
      return newValue;
    });
  };

  const toggleStormforgeMode = () => {
    setIsStormforgeMode(prev => !prev);
  };

  const activateFilmScript = () => {
    // Initialize all three timers immediately
    setFilmScriptState({
      timerOverrides: {
        "Hailey Simmons": {
          baseSeconds: 0, // Start at 0 seconds
          startedAt: Date.now()
        },
        "Colin Reed": {
          baseSeconds: (1 * 60 * 60) + (25 * 60) + 15, // 1h 25m 15s = 5115 seconds
          startedAt: Date.now()
        },
        "Maya Flores": {
          baseSeconds: (25 * 60) + 15, // 25m 15s = 1515 seconds
          startedAt: Date.now()
        }
      },
      timeDisplayOverrides: {},
    });
  };

  const getTimerOverride = (customerName: string): TimerOverride | null => {
    return filmScriptState.timerOverrides[customerName] || null;
  };

  const getTimeDisplayOverride = (customerName: string): TimeDisplayOverride | null => {
    return filmScriptState.timeDisplayOverrides[customerName] || null;
  };

  const notifyTimerStarted = (customerName: string) => {
    // Timers are all set immediately on film mode activation
    // This function can remain for future extensibility
  };

  return (
    <MarketingModeContext.Provider value={{ 
      isMarketingMode,
      isStormforgeMode,
      timerOverrides: filmScriptState.timerOverrides,
      toggleMarketingMode,
      toggleStormforgeMode,
      activateFilmScript,
      getTimerOverride,
      getTimeDisplayOverride,
      notifyTimerStarted
    }}>
      {children}
    </MarketingModeContext.Provider>
  );
}

export function useMarketingMode() {
  const context = useContext(MarketingModeContext);
  if (context === undefined) {
    throw new Error('useMarketingMode must be used within a MarketingModeProvider');
  }
  return context;
}
